import React from 'react';
import { Card, InputNumber, Space, Typography, Tooltip, Divider, Row, Col, Flex, Button } from 'antd';
import { PercentageOutlined, EuroOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const FeesAndTaxes = ({ listingData, handleChange, errors }) => {
    const { t } = useTranslation();

    // Helper to handle InputNumber changes (returns value directly, not event)
    const handleNumberChange = (fieldName) => (value) => {
        handleChange({
            target: {
                name: fieldName,
                value: value || 0
            }
        });
    };

    return (
        <Card
            title={
                <span style={{ fontSize: '16px', fontWeight: 600 }}>
                    <EuroOutlined style={{ marginRight: '8px', color: '#4d6765' }} />
                    {t('admin.listingsForms.feesAndTaxes.title')}
                </span>
            }
            style={{
                marginTop: '24px',
                border: '2px solid #e8e8e8',
                borderRadius: '8px',
            }}
        >
            {/* Info Banner */}
            <div style={{
                padding: '12px 16px',
                backgroundColor: '#e6f4ff',
                borderRadius: '6px',
                marginBottom: '20px',
                border: '1px solid #91caff'
            }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                    💡 <strong>{t('admin.listingsForms.feesAndTaxes.infoLabel')}:</strong>{' '}
                    {t('admin.listingsForms.feesAndTaxes.infoText')}
                </Text>
            </div>

            {/* Percentage-based Taxes */}
            <Title level={5} style={{ marginBottom: '16px', marginTop: 0 }}>
                {t('admin.listingsForms.feesAndTaxes.percentageTaxes')}
            </Title>

            <Row gutter={[24, 16]}>
                <Col xs={24} md={12}>
                    <Flex vertical gap="small" style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: '14px', fontWeight: 500 }}>
                            {t('admin.listingsForms.feesAndTaxes.vatRate')}{' '}
                            <span style={{ color: '#ff4d4f' }}>*</span>
                            <Tooltip title={t('admin.listingsForms.feesAndTaxes.vatTooltip')}>
                                <InfoCircleOutlined style={{ marginLeft: '8px', color: '#1890ff' }} />
                            </Tooltip>
                        </Text>
                        <Space.Compact style={{ width: '100%' }}>
                            <InputNumber
                                name="vat_rate"
                                value={listingData.vat_rate}
                                onChange={handleNumberChange('vat_rate')}
                                style={{ width: '100%' }}
                                size="large"
                                min={0}
                                max={100}
                                precision={2}
                                placeholder="13.00"
                                status={errors?.vat_rate ? 'error' : ''}
                            />
                            <Button size="large" disabled style={{ pointerEvents: 'none' }}>
                                <PercentageOutlined />
                            </Button>
                        </Space.Compact>
                        {errors?.vat_rate && (
                            <Text type="danger" style={{ fontSize: '13px' }}>
                                {errors.vat_rate[0]}
                            </Text>
                        )}
                    </Flex>
                </Col>

                <Col xs={24} md={12}>
                    <Flex vertical gap="small" style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: '14px', fontWeight: 500 }}>
                            {t('admin.listingsForms.feesAndTaxes.municipalityTax')}{' '}
                            <span style={{ color: '#ff4d4f' }}>*</span>
                            <Tooltip title={t('admin.listingsForms.feesAndTaxes.municipalityTooltip')}>
                                <InfoCircleOutlined style={{ marginLeft: '8px', color: '#1890ff' }} />
                            </Tooltip>
                        </Text>
                        <Space.Compact style={{ width: '100%' }}>
                            <InputNumber
                                name="municipality_tax_rate"
                                value={listingData.municipality_tax_rate}
                                onChange={handleNumberChange('municipality_tax_rate')}
                                style={{ width: '100%' }}
                                size="large"
                                min={0}
                                max={100}
                                precision={2}
                                placeholder="1.50"
                                status={errors?.municipality_tax_rate ? 'error' : ''}
                            />
                            <Button size="large" disabled style={{ pointerEvents: 'none' }}>
                                <PercentageOutlined />
                            </Button>
                        </Space.Compact>
                        {errors?.municipality_tax_rate && (
                            <Text type="danger" style={{ fontSize: '13px' }}>
                                {errors.municipality_tax_rate[0]}
                            </Text>
                        )}
                    </Flex>
                </Col>
            </Row>

            <Divider />

            {/* Fixed Fees */}
            <Title level={5} style={{ marginBottom: '16px' }}>
                {t('admin.listingsForms.feesAndTaxes.fixedFees')}
            </Title>

            <Row gutter={[24, 16]}>
                <Col xs={24} md={8}>
                    <Flex vertical gap="small" style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: '14px', fontWeight: 500 }}>
                            {t('admin.listingsForms.feesAndTaxes.climateFee')}{' '}
                            <span style={{ color: '#ff4d4f' }}>*</span>
                            <Tooltip title={t('admin.listingsForms.feesAndTaxes.climateFeeTooltip')}>
                                <InfoCircleOutlined style={{ marginLeft: '8px', color: '#1890ff' }} />
                            </Tooltip>
                        </Text>
                        <Space.Compact style={{ width: '100%' }}>
                            <Button size="large" disabled style={{ pointerEvents: 'none' }}>
                                <EuroOutlined />
                            </Button>
                            <InputNumber
                                name="climate_crisis_fee_per_night"
                                value={listingData.climate_crisis_fee_per_night}
                                onChange={handleNumberChange('climate_crisis_fee_per_night')}
                                style={{ width: '100%' }}
                                size="large"
                                min={0}
                                precision={2}
                                placeholder="1.50"
                                status={errors?.climate_crisis_fee_per_night ? 'error' : ''}
                            />
                            <Button size="large" disabled style={{ pointerEvents: 'none' }}>
                                /night
                            </Button>
                        </Space.Compact>
                        {errors?.climate_crisis_fee_per_night && (
                            <Text type="danger" style={{ fontSize: '13px' }}>
                                {errors.climate_crisis_fee_per_night[0]}
                            </Text>
                        )}
                    </Flex>
                </Col>

                <Col xs={24} md={8}>
                    <Flex vertical gap="small" style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: '14px', fontWeight: 500 }}>
                            {t('admin.listingsForms.feesAndTaxes.cleaningFee')}
                            <Tooltip title={t('admin.listingsForms.feesAndTaxes.cleaningFeeTooltip')}>
                                <InfoCircleOutlined style={{ marginLeft: '8px', color: '#1890ff' }} />
                            </Tooltip>
                        </Text>
                        <Space.Compact style={{ width: '100%' }}>
                            <Button size="large" disabled style={{ pointerEvents: 'none' }}>
                                <EuroOutlined />
                            </Button>
                            <InputNumber
                                name="cleaning_fee"
                                value={listingData.cleaning_fee}
                                onChange={handleNumberChange('cleaning_fee')}
                                style={{ width: '100%' }}
                                size="large"
                                min={0}
                                precision={2}
                                placeholder="0.00"
                                status={errors?.cleaning_fee ? 'error' : ''}
                            />
                        </Space.Compact>
                        {errors?.cleaning_fee && (
                            <Text type="danger" style={{ fontSize: '13px' }}>
                                {errors.cleaning_fee[0]}
                            </Text>
                        )}
                    </Flex>
                </Col>

                <Col xs={24} md={8}>
                    <Flex vertical gap="small" style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: '14px', fontWeight: 500 }}>
                            {t('admin.listingsForms.feesAndTaxes.serviceFee')}
                            <Tooltip title={t('admin.listingsForms.feesAndTaxes.serviceFeeTooltip')}>
                                <InfoCircleOutlined style={{ marginLeft: '8px', color: '#1890ff' }} />
                            </Tooltip>
                        </Text>
                        <Space.Compact style={{ width: '100%' }}>
                            <Button size="large" disabled style={{ pointerEvents: 'none' }}>
                                <EuroOutlined />
                            </Button>
                            <InputNumber
                                name="service_fee"
                                value={listingData.service_fee}
                                onChange={handleNumberChange('service_fee')}
                                style={{ width: '100%' }}
                                size="large"
                                min={0}
                                precision={2}
                                placeholder="0.00"
                                status={errors?.service_fee ? 'error' : ''}
                            />
                        </Space.Compact>
                        {errors?.service_fee && (
                            <Text type="danger" style={{ fontSize: '13px' }}>
                                {errors.service_fee[0]}
                            </Text>
                        )}
                    </Flex>
                </Col>
            </Row>

            {/* Optional: Calculation Preview */}
            {listingData.price > 0 && (
                <>
                    <Divider />
                    <div style={{
                        padding: '16px',
                        backgroundColor: '#f0f5ff',
                        borderRadius: '6px',
                        border: '1px solid #d6e4ff'
                    }}>
                        <Title level={5} style={{ marginTop: 0, marginBottom: '12px' }}>
                            {t('admin.listingsForms.feesAndTaxes.exampleCalculation')}
                        </Title>
                        <Flex vertical gap="small">
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                {t('admin.listingsForms.feesAndTaxes.basedOn')} <strong>€{listingData.price}/night</strong>
                            </Text>
                            <Flex gap="middle" wrap>
                                <Text style={{ fontSize: '13px' }}>
                                    VAT: <strong>€{((listingData.price * (listingData.vat_rate || 0)) / 100).toFixed(2)}</strong>
                                </Text>
                                <Text style={{ fontSize: '13px' }}>
                                    {t('admin.listingsForms.feesAndTaxes.municipalityTax')}: <strong>€{((listingData.price * (listingData.municipality_tax_rate || 0)) / 100).toFixed(2)}</strong>
                                </Text>
                                <Text style={{ fontSize: '13px' }}>
                                    {t('admin.listingsForms.feesAndTaxes.climateFee')}: <strong>€{parseFloat(listingData.climate_crisis_fee_per_night || 0).toFixed(2)}</strong>
                                </Text>
                            </Flex>
                            <Divider style={{ margin: '8px 0' }} />
                            <Text strong style={{ fontSize: '14px', color: '#4d6765' }}>
                                {t('admin.listingsForms.feesAndTaxes.totalPerNight')}: <strong style={{ fontSize: '16px' }}>
                                    €{(
                                        parseFloat(listingData.price) +
                                        (listingData.price * (listingData.vat_rate || 0)) / 100 +
                                        (listingData.price * (listingData.municipality_tax_rate || 0)) / 100 +
                                        parseFloat(listingData.climate_crisis_fee_per_night || 0)
                                    ).toFixed(2)}
                                </strong>
                            </Text>
                        </Flex>
                    </div>
                </>
            )}
        </Card>
    );
};

export default FeesAndTaxes;